// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import {batchActions} from 'utils/batch_actions';

import {PreferenceTypes} from 'mattermost-redux/action_types';
import * as ChannelActions from 'mattermost-redux/actions/channels';
import {savePreferences} from 'mattermost-redux/actions/preferences';
import {getMyChannelMemberships} from 'mattermost-redux/selectors/entities/common';
import {getChannelByName, getUnreadChannelIds, getChannel} from 'mattermost-redux/selectors/entities/channels';
import {getCurrentTeamId, getCurrentRelativeTeamUrl} from 'mattermost-redux/selectors/entities/teams';
import {getCurrentUserId} from 'mattermost-redux/selectors/entities/users';

import {trackEvent} from 'actions/telemetry_actions';
import {loadNewDMIfNeeded, loadNewGMIfNeeded} from 'actions/user_actions';
import {browserHistory} from 'utils/browser_history';
import {getDirectChannelName} from 'utils/utils';
import {loadSidebarForTeam} from 'features/sidebar';
import Constants, {NotificationLevels, Preferences} from 'utils/constants';
import {getBool} from 'mattermost-redux/selectors/entities/preferences';

export function openDirectChannelToUserId(userId) {
    return async (dispatch, getState) => {
        const state = getState();
        const currentUserId = getCurrentUserId(state);
        const channelName = getDirectChannelName(currentUserId, userId);
        const channel = getChannelByName(state, channelName);
        const isDirectChannelShowPreferenceTrusty = getBool(state, Preferences.CATEGORY_DIRECT_CHANNEL_SHOW, userId);

        if (!channel) {
            return dispatch(ChannelActions.createDirectChannel(currentUserId, userId));
        }

        trackEvent('api', 'api_channels_join_direct');

        if (isDirectChannelShowPreferenceTrusty) {
            return {data: channel};
        }

        const now = Date.now();
        const prefDirect = {
            category: Preferences.CATEGORY_DIRECT_CHANNEL_SHOW,
            name: userId,
            value: 'true',
        };
        const prefOpenTime = {
            category: Preferences.CATEGORY_CHANNEL_OPEN_TIME,
            name: channel.id,
            value: now.toString(),
        };
        const actions = [
            {
                type: PreferenceTypes.RECEIVED_PREFERENCES,
                data: [prefDirect],
            },
            {
                type: PreferenceTypes.RECEIVED_PREFERENCES,
                data: [prefOpenTime],
            },
        ];
        dispatch(batchActions(actions));

        dispatch(
            savePreferences(currentUserId, [
                {user_id: currentUserId, ...prefDirect},
                {user_id: currentUserId, ...prefOpenTime},
            ]),
        );

        return {data: channel};
    };
}

export function openGroupChannelToUserIds(userIds) {
    return async (dispatch, getState) => {
        const result = await dispatch(ChannelActions.createGroupChannel(userIds));

        if (result.error) {
            browserHistory.push(getCurrentRelativeTeamUrl(getState()));
        }

        return result;
    };
}

export function loadChannelsForCurrentUser(teamId) {
    return async (dispatch, getState) => {
        const state = getState();
        const unreads = getUnreadChannelIds(state);
        const teamIdToLoad = teamId || getCurrentTeamId(state);

        await dispatch(ChannelActions.fetchMyChannelsAndMembers(teamIdToLoad));

        for (const id of unreads) {
            const channel = getChannel(state, id);
            if (channel && channel.type === Constants.DM_CHANNEL) {
                dispatch(loadNewDMIfNeeded(channel.id));
            } else if (channel && channel.type === Constants.GM_CHANNEL) {
                dispatch(loadNewGMIfNeeded(channel.id));
            }
        }

        await dispatch(loadSidebarForTeam({teamId: teamIdToLoad}));
    };
}

export function searchMoreChannels(term, showArchivedChannels) {
    return async (dispatch, getState) => {
        const state = getState();
        const teamId = getCurrentTeamId(state);

        if (!teamId) {
            throw new Error('No team id');
        }

        const {data, error} = await dispatch(ChannelActions.searchChannels(teamId, term, showArchivedChannels));
        if (data) {
            const myMembers = getMyChannelMemberships(state);

            // When searching public channels, only get channels user is not a member of
            const channels = showArchivedChannels ? data : data.filter((c) => !myMembers[c.id]);
            return {data: channels};
        }

        return {error};
    };
}

export function autocompleteChannels(term, success, error, params) {
    return async (dispatch, getState) => {
        const state = getState();
        const teamId = getCurrentTeamId(state);
        if (!teamId) {
            return {data: false};
        }

        const {data, error: err} = await dispatch(ChannelActions.autocompleteChannels(teamId, term, params));
        if (data && success) {
            success(data);
        } else if (err && error) {
            error({id: err.server_error_id, ...err});
        }

        return {data: true};
    };
}

export function autocompleteChannelsForSearch(term, success, error) {
    return async (dispatch, getState) => {
        const state = getState();
        const teamId = getCurrentTeamId(state);

        if (!teamId) {
            return;
        }

        const {data, error: err} = await dispatch(ChannelActions.autocompleteChannelsForSearch(teamId, term));
        if (data && success) {
            success(data);
        } else if (err && error) {
            error({id: err.server_error_id, ...err});
        }
    };
}

export function addUsersToChannel(channelId, userIds) {
    return async (dispatch) => {
        try {
            const requests = userIds.map((uId) => dispatch(ChannelActions.addChannelMember(channelId, uId)));

            return await Promise.all(requests);
        } catch (error) {
            return {error};
        }
    };
}

export function unmuteChannel(userId, channelId) {
    return ChannelActions.updateChannelNotifyProps(userId, channelId, {
        mark_unread: NotificationLevels.ALL,
    });
}

export function muteChannel(userId, channelId) {
    return ChannelActions.updateChannelNotifyProps(userId, channelId, {
        mark_unread: NotificationLevels.MENTION,
    });
}
